// @filename: todo.ts
import { createModel } from "@rematch/core"
import { RootModel } from "../models"
import { DateTime } from 'luxon'
import Swal from 'sweetalert2/dist/sweetalert2.js'
import axios from 'axios'
import i18next from "i18next"
import { TFunction } from "gatsby-plugin-react-i18next"
import { FormCustomField } from "../../models/tours-calendar.models"
import { BookingDetails } from "../../models/booking.model"

export interface PaymentMethod {
    id: string,
    name: string,
}

export interface BookingState {
    formCustomFields: FormCustomField[]
    previewPass: string,
    tourDateTime: DateTime,
    boatId: string,
    boatName: string,
    ticketsOrdered: number,

    ticketsLeft: number,
    depositPerTicket: number,
    ticketsPerOrder: number,
    paymentMethods: PaymentMethod[],
    translate?: TFunction,
    price?: number,
    projectId?: string,

    // Cancellation-related state
    cancellationDetails?: BookingDetails,
    isFetchingCancellationData: boolean,
    cancellationError?: string,
}

const initalState = {

    tourDateTime: null,
    boatId: '',
    boatName: '',
    ticketsOrdered: 0,

    ticketsLeft: 0,
    depositPerTicket: 15,
    ticketsPerOrder: 6,
    paymentMethods: [],

    translate: null,

    price: null,
    projectId: null,
    formCustomFields: null,
    previewPass: null,

    // Cancellation initial state
    cancellationDetails: null,
    isFetchingCancellationData: false,
    cancellationError: null

} as BookingState;

const ApiEndpoint = "https://wodnawarszawa.pl/api/";

const Api = axios.create();
Api.defaults.headers.post['Content-Type'] = 'application/json';

// i18next-extract-mark-ns-start payment_methods
export const booking = createModel<RootModel>()({
    state: initalState,

    reducers: {
        INIT_FORM(state, payload: {
            boatId: string,
            boatName: string,
            tourDateTime: DateTime,
            ticketsLeft: number,
            translate: TFunction,
            price: number,
            projectId?: string,
            depositPerTicket?: number,
            ticketsPerOrder?: number,
            formCustomFields?: FormCustomField[],
            previewPass?: string
        }) {
            state.boatId = payload.boatId;
            state.boatName = payload.boatName;
            state.tourDateTime = payload.tourDateTime;
            state.ticketsLeft = payload.ticketsLeft;
            state.translate = payload.translate;
            state.price = payload.price;
            state.projectId = payload.projectId;
            state.depositPerTicket = payload.depositPerTicket;
            state.ticketsPerOrder = payload.ticketsPerOrder;
            state.formCustomFields = payload.formCustomFields;
            state.previewPass = payload.previewPass;
        },
        SET_TICKETS(state, quantity: number) {
            state.ticketsOrdered = quantity;
        },
        SET_TICKETS_LEFT(state, quantity: number) {
            state.ticketsLeft = quantity;
        },
        SET_PAYMENT_METHODS(state, paymentMethods: PaymentMethod[]) {
            state.paymentMethods = paymentMethods;
        },
        
        // Cancellation reducers
        SET_CANCELLATION_DATA_LOADING(state, isLoading: boolean) {
            state.isFetchingCancellationData = isLoading;
        },
        SET_CANCELLATION_DETAILS(state, details: BookingDetails) {
            state.cancellationDetails = details;
        },
        SET_CANCELLATION_ERROR(state, error: string) {
            state.cancellationError = error;
        },
        CLEAR_CANCELLATION_DATA(state) {
            state.cancellationDetails = null;
            state.cancellationError = null;
        }
    },

    effects: (dispatch) => ({

        setTickets(quantity: number, rootState) {
            const quantityToSet = quantity <= rootState.booking.ticketsLeft ? quantity : 0;
            dispatch.booking.SET_TICKETS(quantityToSet);
        },

        setTicketsLeft(quantity: number, rootState) {
            dispatch.booking.SET_TICKETS_LEFT(quantity);
        },

        async initForm(payload: { boatId: string, boatName: string, tourDateTime: DateTime, ticketsLeft: number, translate: TFunction, price: number, projectId?: string, depositPerTicket?: number, ticketsPerOrder?: number, formCustomFields?: FormCustomField[], previewPass?: string }, _) {
            dispatch.booking.INIT_FORM({ ...payload });
        },

        async fetchPaymentMethods(translate:TFunction) {

            // request list of method payments 
            const paymentSelectInput: (HTMLSelectElement | null) = (typeof document !== `undefined`) ? document.getElementById("paymentMethod") as HTMLSelectElement : null;

            if (paymentSelectInput == null) return;

            const rawList = [{
                id: "103",
                name: translate("Karta płatnicza")
            }, {
                id: "171",
                name: "Visa Mobile"
            }, {
                id: "166",
                name: "Google Pay"
            }, {
                id: "170",
                name: "Apple Pay"
            }, {
                id: "102",
                name: "Bank Pekao SA"
            }, {
                id: "108",
                name: "PKO Bank Polski"
            }, {
                id: "110",
                name: "Inteligo"
            }, {
                id: "111",
                name: "ING Bank Śląski SA"
            }, {
                id: "113",
                name: "Alior Bank SA"
            }, {
                id: "114",
                name: "Bank Millennium SA"
            }, {
                id: "115",
                name: "Santander Bank Polska SA"
            }, {
                id: "116",
                name: "Credit Agricole Polska SA"
            }, {
                id: "119",
                name: "Getin Bank SA"
            }, {
                id: "124",
                name: "Bank Pocztowy SA"
            }, {
                id: "130",
                name: "Nest Bank"
            }, {
                id: "132",
                name: "Citibank Handlowy SA"
            }, {
                id: "133",
                name: "BNP Paribas Bank Polska SA"
            }, {
                id: "135",
                name: "Banki Spółdzielcze"
            }, {
                id: "145",
                name: "Plus Bank SA"
            }, {
                id: "159",
                name: "Neo Bank"
            }, {
                id: "160",
                name: "mBank"
            }, {
                id: "150",
                name: "BLIK"
            }];

            if (typeof document !== `undefined`)
                rawList.forEach((pm) => {
                    let newOption = document.createElement("option");
                    newOption.text = pm.name;
                    newOption.value = pm.id;
                    paymentSelectInput.add(newOption);
                })

            // Object.keys(rawList).forEach((key) => {
            //     paymentMethodsList.push({ id: rawList[key].id, name: rawList[key].name });
            // });
            // dispatch.booking.SET_PAYMENT_METHODS(paymentMethodsList);
        },

        async submitForm(payload: { formElement: HTMLFormElement, language?: string }, rootState) {
            const form = payload.formElement;
            const language = payload.language;

            if (form.checkValidity()) {

                form.classList.remove('was-validated');

                // retrieve data from form inputs
                const formInputs = form.getElementsByTagName('input');
                let formDataset = {};
                for (let i = 0; i < formInputs.length; i++) {
                    formDataset[formInputs[i].id] = formInputs[i].value;
                }

                // get rest of booking data from store
                const boatId = rootState.booking.boatId;
                const tourDateTime = rootState.booking.tourDateTime;
                const previewPass = rootState.booking.previewPass;
                const depositPerTicket = rootState.booking.depositPerTicket;


                const tourID = boatId + "." + tourDateTime.toFormat('ddLLyy') + "." + tourDateTime.toFormat('HHmm');
                const paymentMethod = (form.querySelector('[id="paymentMethod"]') as HTMLFormElement | null)?.value;

                const tourIsPayable = rootState.booking.price != null;

                const tourIsFree = !tourIsPayable && (!depositPerTicket || depositPerTicket <= 0);

                // Create an object with custom fields where keys are displayNames and values are form element values
                let customFields = {};
                if (rootState.booking.formCustomFields) {
                    rootState.booking.formCustomFields.forEach(field => {
                        const formElement = form.querySelector(`[id="${field.id}"]`) as HTMLFormElement | null;
                        if (formElement) {
                            customFields[field.displayName] = formElement.value;
                        }
                    });
                }

                const payload = {
                    variant: tourIsFree ? "addBooking" : (tourIsPayable ? "addBookingWithTransaction" : "addBookingWithDeposit"),
                    fields: {
                        tourID: tourID,
                        tickets: formDataset['ticketsAmount'],
                        name: formDataset['fullName'],
                        phone: formDataset['phone'],
                        email: formDataset['mail'],
                        groupId: paymentMethod,
                        language: language ?? "pl",
                        projectId: rootState.booking.projectId,
                        customFields: customFields,
                        previewPass: previewPass
                    }
                }
                Swal.fire(alerts.formSubmitInfo(rootState.booking.translate, tourIsFree));

                const response = await axios.post(ApiEndpoint, payload);
                const jsonResponse = response.data;

                if (response.status == 200 && jsonResponse?.success) {
                    form.reset();
                    if (tourIsFree) Swal.fire(alerts.formSubmitSuccess(rootState.booking.translate));
                    if (typeof window !== `undefined`) {
                        if (tourIsFree) {
                            setTimeout(() => {
                                window.location.href = '/';
                            }, 7000);
                        } else
                            window.location = jsonResponse.paymentUrl;
                    }
                } else {
                    if (response.status == 200) {
                        if (jsonResponse?.error && jsonResponse?.code == "TOUR_TICKETS_LIMIT_EXCEEDED" && typeof jsonResponse?.ticketsLeft !== `undefined`) {
                            if (jsonResponse?.ticketsLeft <= 0) {
                                Swal.fire(alerts.noTicketsLeft(rootState.booking.translate));
                                dispatch.booking.SET_TICKETS_LEFT(0);
                                dispatch.booking.SET_TICKETS(0);
                            } else {
                                Swal.fire(alerts.ticketsLimitExceeded(rootState.booking.translate, jsonResponse?.ticketsLeft))
                                dispatch.booking.SET_TICKETS_LEFT(jsonResponse?.ticketsLeft);
                                dispatch.booking.SET_TICKETS(jsonResponse?.ticketsLeft);
                            }
                        }
                    } else {
                        Swal.fire(alerts.formSubmitError(rootState.booking.translate));
                    }
                }
            } else {
                Swal.fire(alerts.formHasErrors(rootState.booking.translate));
                form.classList.add('was-validated');

            }


        },

        // Cancellation effects
        async fetchBookingDetails(payload: { bookingId: string }, rootState) {
            const { bookingId } = payload;
            dispatch.booking.SET_CANCELLATION_DATA_LOADING(true);
            
            try {
                const requestPayload = {
                    variant: "getBookingDetails",
                    fields: {
                        bookingId: decodeURIComponent(bookingId)
                    }
                };

                const response = await axios.post(ApiEndpoint, requestPayload);
                
                if (response.data.success && response.data.booking) {
                    // Convert ISO string to Date object
                    const bookingData = {
                        ...response.data.booking,
                        tourDateTime: new Date(response.data.booking.tourDateTime)
                    };
                    dispatch.booking.SET_CANCELLATION_DETAILS(bookingData);
                } else {
                    dispatch.booking.SET_CANCELLATION_ERROR("Could not fetch booking details");
                }
            } catch (error) {
                dispatch.booking.SET_CANCELLATION_ERROR("Failed to load booking details");
            } finally {
                dispatch.booking.SET_CANCELLATION_DATA_LOADING(false);
            }
        },


        async cancelBooking(payload: { bookingId: string, translate: TFunction }, rootState) {
            const { bookingId, translate } = payload;
            try {
                Swal.fire({
                    icon: 'info',
                    title: translate('Processing cancellation...'),
                    text: translate('Please wait while we cancel your booking'),
                    allowOutsideClick: false,
                    showConfirmButton: false,
                });
                
                const requestPayload = {
                    variant: "cancelBooking",
                    fields: {
                        bookingId: decodeURIComponent(bookingId)
                    }
                };

                const response = await axios.post(ApiEndpoint, requestPayload);
                
                if (response.data.success) {
                    Swal.fire({
                        icon: 'success',
                        title: translate('Booking cancelled successfully'),
                        text: response.data.success || translate('Your booking has been cancelled. A confirmation email will be sent shortly.'),
                        confirmButtonText: translate('Close'),
                        allowOutsideClick: false
                    }).then(() => {
                        if (typeof window !== `undefined`) {
                            window.location.href = '/';
                        }
                    });
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: translate('Cancellation failed'),
                        text: response.data.error || translate('We could not cancel your booking. Please contact support.'),
                        confirmButtonText: translate('Close'),
                        allowOutsideClick: false
                    });
                }
            } catch (error) {
                Swal.fire({
                    icon: 'error',
                    title: translate('Error occurred'),
                    text: translate('A network error occurred. Please try again or contact support.'),
                    confirmButtonText: translate('Close'),
                    allowOutsideClick: false
                });
            }
        }
    }),
})

type SwalAlertConfig = {
    title: string,
    text: string,
    icon: 'error' | 'warning' | 'success' | 'info' | 'question',
    confirmButtonText?: string,
    allowOutsideClick: boolean,
    showConfirmButton?: boolean,

}

// i18next-extract-mark-ns-start booking_alerts
const alerts: { [key: string]: (t: TFunction, param?) => SwalAlertConfig } = {
    formSubmitSuccess: (t) => {
        return {
            icon: 'success',
            title: t('Gotowe, rezerwacja przyjęta!'),
            text: t('Dziękujemy za dokonanie rezerwacji. Potwierdzenie zostało wysłane na adres e-mail podany w formularzu. Za chwilę przekierujemy Cię na stronę główną'),
            allowOutsideClick: false,
            showConfirmButton: false,
            timer: 7000,
            timerProgressBar: true,
            didClose: () => {
                window.location.href = '/'; // Redirect to homepage
            }
        }
    },
    formSubmitInfo: (t, tourIsFree: boolean) => {
        return {
            icon: 'info',
            title: t(tourIsFree ? 'Wysyłanie rezerwacji...' : 'Kieruję do płatności...'),
            text: t(tourIsFree ? 'Trwa przesyłanie i weryfikacja Twojej rezerwacji. Prosimy o chwilę cierpliwości' : 'Za chwilę nastapi przekierowanie do strony płatności. Prosimy czekać...'),
            allowOutsideClick: false,
            showConfirmButton: false,
        }
    },
    formSubmitError: (t) => {
        return {
            icon: 'error',
            title: t('Coś poszło nie tak...'),
            text: t('Wystąpił błąd podczas rezerwacji. Prosimy spróbować ponownie lub zgłosić błąd na kontakt@wodnawarszawa.pl'),
            allowOutsideClick: true,
        }
    },
    formHasErrors: (t) => {
        return {
            icon: 'warning',
            title: t('Błędy formularza'),
            text: t('Formularz zawiera błędy lub braki. Sprawdź i wyślij ponownie'),
            allowOutsideClick: true,
        }
    },
    checkingAvailability: (t) => {
        return {
            icon: 'info',
            title: t('Sprawdzam dostępność...'),
            text: t('Sprawdzam aktualną dostępność miejsc na wybraną atrakcję. Prosimy czekać...'),
            allowOutsideClick: false,
            showConfirmButton: false,
        }
    },
    noTicketsLeft: (t) => {
        return {
            icon: 'error',
            title: t('Brak wolnych miejsc'),
            text: t('Przykro nam, ale nie ma już wolnych miejsc na atrakcję. Zapraszamy na inne atrakcje!'),
            confirmButtonText: t('Zamknij'),
            allowOutsideClick: false
        }
    },
    ticketsLimitExceeded: (t, ticketsLeft) => {
        return {
            icon: 'error',
            title: t('Przekroczono ilość dostępnych miejsc'),
            text: t(`W momencie wysłania rezerwacji liczba dostępnych miejsc okazała się mniejsza niż ta którą chcesz zarezerwować. Zmniejszyliśmy ilość rezerwowanych miejsc w Twoim formularzu do ${ticketsLeft}.`),
            confirmButtonText: t('Zamknij'),
            allowOutsideClick: false
        }
    }
}